import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatCents } from '@pirouette/shared';
import {
  useFamilyCharges,
  useFamilyPayments,
  useFamilyAccounts,
  useFamilyAccountBalances,
  type ChargeWithAllocations,
} from '@/data/billing';
import { useCreateCheckoutSession } from '@/data/stripe';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/billing')({
  component: ParentBilling,
});

function useMyFamilyId() {
  return useQuery({
    queryKey: ['my-family-id'],
    queryFn: async (): Promise<string | null> => {
      const userResp = await supabase.auth.getUser();
      const familyId = userResp.data.user?.app_metadata.family_id as string | undefined;
      return familyId ?? null;
    },
  });
}

function ParentBilling() {
  const myFamily = useMyFamilyId();
  const familyId = myFamily.data ?? undefined;
  const accounts = useFamilyAccounts(familyId);
  const balances = useFamilyAccountBalances(familyId);
  const charges = useFamilyCharges(familyId);
  const payments = useFamilyPayments(familyId);
  const checkout = useCreateCheckoutSession();

  // Per-account selection map: account_id -> Set<charge_id>
  const [selectedByAccount, setSelectedByAccount] = useState<Record<string, Set<string>>>({});

  const accountList = accounts.data ?? [];
  const balanceList = balances.data ?? [];
  const allOpenCharges = (charges.data ?? []).filter(
    (c) => c.status === 'pending' || c.status === 'partial',
  );
  const paidCharges = (charges.data ?? []).filter((c) => c.status === 'paid');

  function toggleSelection(accountId: string, chargeId: string) {
    setSelectedByAccount((prev) => {
      const next = { ...prev };
      const set = new Set(next[accountId] ?? []);
      if (set.has(chargeId)) set.delete(chargeId);
      else set.add(chargeId);
      next[accountId] = set;
      return next;
    });
  }

  async function payAccount(chargeIds: string[]) {
    if (chargeIds.length === 0) return;
    try {
      const success = `${window.location.origin}/parent/billing/checkout-return?mode=charges&status=success`;
      const cancel = `${window.location.origin}/parent/billing`;
      const resp = await checkout.mutateAsync({
        mode: 'charges',
        charge_ids: chargeIds,
        success_url: success,
        cancel_url: cancel,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't start checkout",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Your account
        </p>
        <h1 className="mt-2">Billing</h1>
        <nav className="mt-3 flex flex-wrap gap-3 text-[var(--text-sm)]">
          <Link to="/parent/billing/auto-pay" className="underline">
            Auto-pay
          </Link>
          <Link to="/parent/billing/payment-methods" className="underline">
            Payment methods
          </Link>
          <Link to="/parent/billing/tickets" className="underline">
            Tickets
          </Link>
        </nav>
      </header>

      {/* Per-account balance cards — one row per account. */}
      <section aria-labelledby="balances-heading">
        <h2 id="balances-heading" className="sr-only">
          Account balances
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {balanceList.map((b) => (
            <article
              key={b.account_id}
              className="surface-elevated p-6"
              aria-labelledby={`bal-${b.account_id}`}
            >
              <p
                id={`bal-${b.account_id}`}
                className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase"
              >
                {b.name}
              </p>
              <p
                className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums"
                style={{
                  color: b.outstanding_cents > 0 ? 'var(--color-danger)' : 'var(--color-success)',
                }}
              >
                {formatCents(b.outstanding_cents)}
              </p>
              <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                {b.open_count} open
                {b.overdue_count > 0 && ` · ${b.overdue_count} overdue`}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* One section per account with its own open charges + Pay button. */}
      {accountList.length === 0 && (
        <Empty
          title="No billing accounts yet"
          description="Your studio hasn't set up your billing accounts. Reach out and they'll get this sorted."
        />
      )}

      {accountList.map((account) => {
        const accountCharges = allOpenCharges.filter((c) => c.account_id === account.id);
        const selectedSet = selectedByAccount[account.id] ?? new Set<string>();
        const selectedTotal = accountCharges
          .filter((c) => selectedSet.has(c.id))
          .reduce((s, c) => s + c.remaining_cents, 0);

        return (
          <section key={account.id} aria-labelledby={`acct-${account.id}`} className="space-y-4">
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-[var(--color-border)] pb-2">
              <h2 id={`acct-${account.id}`} className="text-[var(--text-xl)]">
                {account.name}{' '}
                <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  · {account.kind}
                </span>
              </h2>
              {selectedSet.size > 0 && (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
                  {selectedSet.size} selected · {formatCents(selectedTotal)}
                </p>
              )}
            </div>

            {accountCharges.length === 0 ? (
              <p className="text-[var(--color-ink-muted)]">Nothing due on this account.</p>
            ) : (
              <>
                <ul className="grid gap-2">
                  {accountCharges.map((c) => {
                    const isSelected = selectedSet.has(c.id);
                    return (
                      <li
                        key={c.id}
                        className="surface flex items-center justify-between gap-3 p-4"
                      >
                        <label className="flex flex-1 cursor-pointer items-center gap-3">
                          <input
                            type="checkbox"
                            className="size-5 accent-[var(--color-accent)]"
                            checked={isSelected}
                            onChange={() => toggleSelection(account.id, c.id)}
                            aria-label={`Select ${c.description} for payment`}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{c.description}</p>
                            <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                              <Badge tone="neutral">{c.category}</Badge>{' '}
                              {c.due_date ? `due ${c.due_date}` : 'no due date'}
                            </p>
                          </div>
                        </label>
                        <div className="text-right">
                          <p className="font-medium tabular-nums">
                            {formatCents(c.remaining_cents)}
                          </p>
                          {c.status === 'partial' && (
                            <p className="text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                              of {formatCents(c.amount_cents)} ({formatCents(c.allocated_cents)}{' '}
                              paid)
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={() => void payAccount([...selectedSet])}
                    disabled={selectedSet.size === 0 || checkout.isPending}
                  >
                    {checkout.isPending
                      ? 'Starting checkout…'
                      : `Pay selected ${account.name.toLowerCase()} (${formatCents(selectedTotal)})`}
                  </Button>
                  <button
                    type="button"
                    className="text-[var(--color-ink-muted)] text-[var(--text-sm)] underline hover:text-[var(--color-ink)]"
                    onClick={() => {
                      setSelectedByAccount((prev) => ({
                        ...prev,
                        [account.id]: new Set(accountCharges.map((c) => c.id)),
                      }));
                    }}
                  >
                    Select all
                  </button>
                </div>
              </>
            )}
          </section>
        );
      })}

      {paidCharges.length > 0 && (
        <section aria-labelledby="paid-charges">
          <h2 id="paid-charges" className="text-[var(--text-xl)]">
            Recently paid
          </h2>
          <ul className="mt-4 grid gap-2">
            {paidCharges.slice(0, 10).map((c) => (
              <li key={c.id} className="surface flex items-center justify-between p-3">
                <span className="text-[var(--text-sm)]">
                  {c.description}
                  <span className="ml-2 text-[var(--color-ink-subtle)]">· {c.account_name}</span>
                </span>
                <Badge tone="success">{formatCents(c.amount_cents)}</Badge>
              </li>
            ))}
          </ul>
        </section>
      )}

      {payments.data && payments.data.length > 0 && (
        <section aria-labelledby="payment-history">
          <h2 id="payment-history" className="text-[var(--text-xl)]">
            Payment history
          </h2>
          <ul className="mt-4 grid gap-2">
            {payments.data.slice(0, 20).map((p) => (
              <li key={p.id} className="surface flex items-center justify-between p-3">
                <div>
                  <p className="font-medium tabular-nums">{formatCents(p.amount_cents)}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {p.method} · {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <Badge
                  tone={
                    p.status === 'succeeded'
                      ? 'success'
                      : p.status === 'failed'
                        ? 'danger'
                        : p.status === 'refunded'
                          ? 'warn'
                          : 'neutral'
                  }
                >
                  {p.status}
                </Badge>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// Re-export for routes that consume the type.
export type { ChargeWithAllocations };
